/**
 * normalizeExtract — the adapter between a structured_json artifact and the
 * Structured tab (spec §3; data shapes in EXTRACT_VIEWER.md §5).
 *
 *   shape A — versioned envelope `{ version, extractor_name, …, payload }`
 *   shape B — raw payload (Sizer, legacy CU)
 *   shape C — a user-edited copy of A or B (served by extract_bytes by default)
 *
 * Also exports the payload unwrap the Review panes use, so both tabs read the
 * same object.
 */

export const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/** Shape A: an extractor name / schema class AND an object payload. */
export const isEnvelope = (raw) =>
  isPlainObject(raw) &&
  (typeof raw.schema_class === "string" || typeof raw.extractor_name === "string") &&
  isPlainObject(raw.payload);

/** The extractor's own output, whichever outer shape the file has. */
export const unwrapExtractPayload = (raw) => (isEnvelope(raw) ? raw.payload : raw);

/**
 * Route key for a docType — how configs are found when the file has no
 * `schema_class` (shape B), and what `reviewKindForDocType` builds on.
 */
export const routeFor = (docType) => {
  let d = String(docType || "").trim();
  if (d.startsWith("Source Point - ")) d = d.slice("Source Point - ".length);
  if (d.startsWith("Unexecuted ")) d = d.slice("Unexecuted ".length);
  d = d.toLowerCase();
  if (!d) return null;
  if (/background/.test(d)) return "background";
  if (/promissory|note$/.test(d)) return "note";
  if (/budget|scope of work|\bsow\b/.test(d)) return "budget";
  if (/sizer|term sheet/.test(d)) return "sizer";
  if (/photo\s*id|identification|driver'?s?\s*licen|passport|\bid\b/.test(d)) return "id";
  return null;
};

/** Facts about an index row the Structured tab needs even without a JSON file. */
export const rowFacts = (row) => {
  const parent = row?.parent || null;
  const prov = row?.parent_provenance || {};
  const extracts = row?.extracts || {};
  const docType = parent?.docType || prov.docType || null;
  return {
    docType,
    docGroup: parent?.docGroup || prov.docGroup || null,
    route: routeFor(docType),
    json: (extracts.structured_json || [])[0] || null,
    hasMarkdown: (extracts.markdown || []).length > 0,
    imageCount: (extracts.image || []).length,
    rejection: parent?.extractionRejection || null,
    sourceKind: row?.source_kind || "document",
  };
};

/**
 * @param {string|object} rawText the artifact bytes (getText returns a raw string)
 * @param {object} row the extracts-index row
 * @returns {object} NormalizedExtract, or `{ error: "parse", message }`
 */
export function normalizeExtract(rawText, row) {
  let parsed;
  try {
    parsed = typeof rawText === "string" ? JSON.parse(rawText) : rawText;
  } catch (e) {
    return { error: "parse", message: e?.message || "Invalid JSON" };
  }
  const facts = rowFacts(row);
  const shapeA = isEnvelope(parsed);
  let payload = shapeA ? parsed.payload : parsed;
  if (!isPlainObject(payload)) payload = { value: payload };
  const env = shapeA ? parsed : {};
  return {
    shape: shapeA ? "A" : "B",
    envelope: {
      version: env.version ?? null,
      extractor_name: env.extractor_name ?? null,
      extractor_version: env.extractor_version ?? null,
      schema_class: env.schema_class ?? null,
      written_at: env.written_at ?? null,
      edited_by: env.edited_by ?? null,
    },
    payload,
    raw: parsed,
    docType: facts.docType,
    docGroup: facts.docGroup,
    route: facts.route,
    blobName: facts.json?.blob_name || "",
    indexVersion: facts.json?.version ?? null,
    hasMarkdown: facts.hasMarkdown,
    imageCount: facts.imageCount,
    rejection: facts.rejection,
  };
}

export default normalizeExtract;
